<template>
  <div>
    <Loader :messageSelector="messageSelector" :overlay="overlay" />

    <v-form ref="form" v-model="valid">
      <!-- Autorisation de pratique -->
      <fieldset>
        <legend class="text-h6 text-sm-h5">Autorisation(s) de pratique</legend>

        <RessourceInfoBulle code="RE-AP-1" />

        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Au besoin, retirer ou réajouter vos autorisations de pratique</th>
                <th class="text-left"></th>
                <th class="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in listeAutorisationsDePratique.filter((a) => a.demandeInscription && a.demandeInscription.length > 0)"
                :key="item.name"
                :class="{ 'red lighten-3': isRetraitVolontaire(item) }"
              >
                <td class="pt-4">
                  <v-autocomplete
                    v-model="item.categorieAutorisation"
                    :item-text="itemText"
                    :item-value="itemValue"
                    :items="listeCategoriesAutorisationsFilter"
                    :rules="rules.categorieAutorisation"
                    disabled
                    label="Catégorie d'autorisation"
                    required
                    return-object
                  ></v-autocomplete>
                </td>
                <td class="pt-4">
                  <v-autocomplete
                    v-model="item.autorisation"
                    :item-text="itemText"
                    :item-value="itemValue"
                    :items="listeFilteredAutorisations(item.categorieAutorisation.id)"
                    :rules="rules.autorisation"
                    disabled
                    label="Autorisation"
                    required
                    return-object
                  ></v-autocomplete>
                </td>
                <td class="text-right pt-4">
                  <div v-if="isRetraitVolontaire(item)" @click="addRow(item)">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn color="primary" icon v-bind="attrs" v-on="on">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </template>
                      <span>Réactiver le renouvellement de cette autorisation de pratique</span>
                    </v-tooltip>
                  </div>
                  <div v-else-if="canDelete(item)">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn color="#8b0000" icon v-bind="attrs" @click="afficherAlert(item)" v-on="on">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Ne pas renouveler cette autorisation de pratique</span>
                    </v-tooltip>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </fieldset>

      <fieldset v-if="legalSpecial" class="legalSection">
        <legend class="text-h6 text-sm-h5">Conditions et critères</legend>
        <section class="mentionLegal">
          L'autorisation spéciale temporaire
          <b>s'adresse aux personnes qui répondent aux conditions d'admissibilité</b>
          suivantes :
          <ol>
            <li>Être titulaire d'un baccalauréat ou d'une maîtrise en T.S. ou d'une maîtrise en T.C.F. OU</li>
            <li>
              Être légalement autorisé à exercer la profession de travailleur social ou de thérapeute conjugal et familial hors du Québec
              (titulaire d'un permis) et être en règle avec l'organisme ayant délivré l'autorisation d'exercice OU
            </li>
            <li>
              Être invité par un établissement, une université ou un autre organisme dans le but d'apprendre ou d'enseigner des activités
              réservées OU
            </li>
            <li>
              Être invité par un établissement, une université ou un autre organisme dans le but d'exercer de façon ponctuelle auprès d'une
              personne ou d'un groupe de personnes ciblées;
            </li>
            <li>Être amené à offrir temporairement des services au Québec.</li>
            <li>L'autorisation spéciale est valable pour une période d'un an ou moins (selon la durée recherchée).</li>
          </ol>
          <br />
          <p class="font-italic">
            Attention : lorsqu'un candidat est invité à venir au Québec pour donner des services à la population, la voie à favoriser est
            celle du permis d'exercice. Pour la personne légalement autorisée à exercer hors Québec et qui est par ailleurs titulaire d'un
            permis d'exercice au Québec sans être inscrit au tableau de l'Ordre,
            <b>la réinscription au tableau de l'Ordre doit être privilégiée.</b>
          </p>
          <p>
            Retrouver plus d'informations sur
            <a
              href="https://www1.otstcfq.org/exercer-au-quebec/exercer-au-quebec/admission-et-inscription/autorisation-speciale-temporaire/"
              target="_blank"
              >l'autorisation spéciale temporaire</a
            >
            sur notre site internet.
          </p>
        </section>
      </fieldset>

      <fieldset v-if="legalEtudiant" class="legalSection">
        <legend class="text-h6 text-sm-h5">Condition étudiant</legend>
        <p class="mentionLegal">
          Une inscription à ce registre permet l'exercice, par des étudiants inscrits aux programmes universitaires du Québec, de certaines
          activités professionnelles réservées aux membres de l'Ordre dans le cadre d'un emploi rémunéré et sous supervision.
        </p>
        <p class="mentionLegal">
          Cette autorisation temporaire prendra fin à la première occurrence, soit l'obtention du grade donnant accès au permis de l'Ordre,
          la fin du contrat de travail ou l'abandon du programme d'études.
          <br />
          Retrouver plus d'information sur
          <a
            href="https://www1.otstcfq.org/exercer-au-quebec/exercer-au-quebec/admission-et-inscription/registre-des-etudiants/"
            target="_blank"
            >le registre des étudiants</a
          >
          sur notre site internet.
        </p>
      </fieldset>

      <!-- Autre Autorisation de pratique -->
      <fieldset v-if="!isDroitsAcquis">
        <legend class="text-h6 text-sm-h5">Autre(s) autorisation(s) de pratique</legend>
        <section class="body-2">
          <RessourceInfoBulle code="RE-AP-2" />
        </section>

        <!-- NumeroDePermisDePsychotherapeuteSiApplicable -->
        <v-text-field
          v-model="getAutresAutorisationsDePratique.numeroDePermisDePsychotherapeuteSiApplicable"
          label="Numéro de permis de psychothérapeute, si applicable"
        ></v-text-field>

        <v-autocomplete
          v-model="getAutresAutorisationsDePratique.membreDUnAutreOrdre"
          :item-text="itemText"
          :item-value="itemValue"
          :items="listesAutresOrdres"
          clearable
          label="Membre d'un ou plusieurs ordres du Québec (n.b. si vous êtes uniquement psychothérapeute, vous n'êtes pas considéré être membre de l'Ordre des psychologues)"
          multiple
          return-object
        ></v-autocomplete>

        <!-- Citoyennete -->
        <v-autocomplete
          v-model="getAutresAutorisationsDePratique.instancesReglementairesEnTravailSocialHorsQuebec"
          :item-text="itemText"
          :item-value="itemValue"
          :items="listesInstancesReglementaires"
          clearable
          label="Instances règlementaires en Travail social hors Québec"
          multiple
          return-object
        ></v-autocomplete>
      </fieldset>
    </v-form>

    <div class="text-center mt-6 d-flex justify-space-between">
      <v-btn color="primary" @click="back()">Retour</v-btn>
      <v-btn :disabled="!valid" color="primary" @click="saveAndContinue()">Suivant</v-btn>

      <!-- CAS-3193 : le retrait de la dernière autorisation vaut démission, le popup porte le même formulaire que la page dédiée -->
      <v-dialog v-model="dialog" max-width="700" persistent>
        <v-card>
          <v-card-text class="subtitle-1 pt-4">
            <TextesDemission />
            <v-form ref="formDemission" v-model="demissionValide">
              <ChampsDemission v-model="champsDemission" />
            </v-form>
            <RessourceInfoBulle code="DE-POPUP" />
          </v-card-text>
          <v-card-actions>
            <v-btn color="error" text @click="annulerDemission()"> Annuler</v-btn>
            <v-spacer></v-spacer>
            <v-btn :disabled="!demissionValide" color="primary" text @click="processRetraitVolontaire"> Je souhaite me retirer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogRetraitAutorisationUnique" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline"> Attention</v-card-title>
          <v-card-text v-if="!isDroitsAcquis" class="subtitle-1">
            <p>
              Vous êtes sur le point de vous retirer du tableau de l'ordre pour l'autorisation de pratique sélectionnée en date du
              <span class="font-weight-bold">1er avril.</span>
            </p>
            <p>
              Pour plus d'information sur la cessation d'exercice et sur les règles qui s'appliquent, consultez
              <a
                href="https://www1.otstcfq.org/membres/pratiques-professionnelles/faq-pratiques-professionnelles/#cessation"
                target="_blank"
                >cette section</a
              >
              de la FAQ sur les pratiques professionnelles.
            </p>
            <div class="font-weight-bold red--text">
              <p>
                Veuillez noter que si vous demandez à vous réinscrire au Tableau de l'Ordre, vous devrez payer des frais de réinscription et
                vous serez assujetti aux autres conditions en vigueur au moment de votre retour, notamment, si plus de 5 ans s'écoulent
                entre votre retrait du Tableau et votre demande de réinscription, un stage de perfectionnement pourrait vous être imposé.
              </p>
              <p>
                Après avoir cliqué sur "Je souhaite me retirer", vous devrez cliquer sur le bouton "suivant" afin que votre demande soit
                enregistrée.
              </p>
            </div>
          </v-card-text>
          <v-card-text v-else class="subtitle-1">
            Vous êtes sur le point de vous retirer du registre des bénéficiaires des droits acquis pour l'autorisation de pratique
            sélectionnée en date du <span class="font-weight-bold">1er avril.</span>
          </v-card-text>
          <v-card-actions>
            <v-btn color="error" text @click="dialogRetraitAutorisationUnique = false"> Annuler</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="deleteRow()"> Je souhaite me retirer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import Loader from "../Loader"
import TextesDemission from "@/components/Demission/TextesDemission"
import ChampsDemission from "@/components/Demission/ChampsDemission"
import NavigationPageFormulaireInscriptionMixin from "@/mixins/navigationPageFormulaireInscriptionMixin"
import ParamFormulaire from "@/mixins/paramFormulaire"
import { isDroitsAcquis } from "@/utils/autorisationHelper"

export default {
  mixins: [NavigationPageFormulaireInscriptionMixin, ParamFormulaire],
  components: {
    Loader: Loader,
    TextesDemission: TextesDemission,
    ChampsDemission: ChampsDemission,
  },
  props: {
    routeName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      overlay: false,
      messageSelector: "",
      valid: false,
      rules: {
        CategorieAutorisation: [(v) => (!!v && v.length != 0) || "Le type d'autorisation est requis"],
        Autorisation: [(v) => (!!v && v.length != 0) || "L'autorisation est requise"],
      },
      dialog: false,
      dialogRetraitAutorisationUnique: false,
      autorisationASupprimer: null,
      demissionValide: false,
      champsDemission: {
        raisonDuRetrait: null,
        reinscriptionPrevue: null,
        precisionRetrait: null,
      },
    }
  },
  computed: {
    ...mapGetters({
      demandeInscriptionId: "DemandeInscription/GET_DEMANDEINSCRIPTIONID",
      getAutorisations: "AutorisationDePratique/GET_AUTORISATIONSDEPRATIQUE",
      listeCategories: "Catalogue/GET_CATEGORIEAUTORISATION",
      listeFilteredAutorisations: "Catalogue/GET_AUTORISATIONS_BY_CATEGORIEAUTORISATION",
      getAutresAutorisationsDePratique: "AutresAutorisationsDePratique/GET_AUTRESAUTORISATIONDEPRATIQUES",
      listesAutresOrdres: "Catalogue/GET_AUTRESORDRE",
      listesInstancesReglementaires: "Catalogue/GET_INSTANCESREGLEMENTAIRES",
      getFormations: "FormationInitiale/GET_FORMATIONS",
    }),
    legalSpecial() {
      return (
        this.getAutorisations &&
        this.getAutorisations.some(
          (t) => t.categorieAutorisation && t.categorieAutorisation.id == this.$catalogues.categorieAutorisation.autorisationSpeciale
        )
      )
    },
    legalEtudiant() {
      return (
        this.getAutorisations &&
        this.getAutorisations.some(
          (t) => t.categorieAutorisation && t.categorieAutorisation.id == this.$catalogues.categorieAutorisation.registreDesEtudiants
        )
      )
    },
    listeAutorisationsDePratique() {
      return this.getAutorisations.filter((a) => a.statut?.id === this.$catalogues.statutAutorisationDePatique.membre || a.statut?.id === this.$catalogues.statutAutorisationDePatique.droitsAcquis)
    },
    listeCategoriesAutorisationsFilter() {
      return this.listeCategories.sort((a, b) => (a.lang00 < b.lang00 ? 1 : -1))
    },
    isDroitsAcquis() {
      return isDroitsAcquis(this.getAutorisations)
    },
  },
  async created() {
    this.fetchItems()
    // Textes configurables du formulaire de démission, ajoutés aux ressources du renouvellement
    await this.loadParams(this.$paramFormulaire.codeFormulaireDemission, true)
  },
  methods: {
    ...mapActions({
      getCatalogue: "Catalogue/getCatalogue",
      updateAutorisationDePratique: "AutorisationDePratique/updateAutorisationDePratique",
      getAutorisationDePratiqueByDemandeInscriptionId: "AutorisationDePratique/getAutorisationDePratiqueByDemandeInscriptionId",
      retraitVolontaire: "DemandeInscription/retraitVolontaire",
      demission: "DemandeInscription/demission",
      updateAutresAutorisationsDePratique: "AutresAutorisationsDePratique/updateAutresAutorisationsDePratique",
      createAutresAutorisationsDePratique: "AutresAutorisationsDePratique/createAutresAutorisationsDePratique",
      getAutresAutorisationsDePratiqueByDemandeInscriptionId:
        "AutresAutorisationsDePratique/getAutresAutorisationsDePratiqueByDemandeInscriptionId",
      getDocumentsByDemandeInscriptionId: "Documents/getDocumentsByDemandeInscriptionId",
      demandeTerminee: "DemandeInscription/demandeTerminee",
    }),
    async fetchItems() {
      try {
        this.messageSelector = "Loading"
        this.overlay = true

        const requests = [
          this.getCatalogue(this.$cataloguesDesc.categorieAutorisation),
          this.getCatalogue(this.$cataloguesDesc.statutAutorisationDePatique),
          this.getCatalogue(this.$cataloguesDesc.autorisationDePatique),
          this.getCatalogue(this.$cataloguesDesc.membreAutresOrdres),
          this.getCatalogue(this.$cataloguesDesc.instancesReglementaires),
        ]

        if (this.demandeInscriptionId) {
          requests.push(this.getAutorisationDePratiqueByDemandeInscriptionId(this.demandeInscriptionId))
          requests.push(this.getAutresAutorisationsDePratiqueByDemandeInscriptionId(this.demandeInscriptionId))
          requests.push(this.getDocumentsByDemandeInscriptionId(this.demandeInscriptionId))
        }

        await Promise.all(requests)

        this.overlay = false
      } catch (erreur) {
        console.log(erreur)
      }
    },
    saveAndContinue() {
      if (this.listeAutorisationsDePratique.filter((a) => !this.isRetraitVolontaire(a)).length == 0) {
        this.dialog = true
        return
      }

      this.save()
    },
    async save() {
      this.validateForm()
      if (!(this.valid && this.getAutresAutorisationsDePratique && this.listeAutorisationsDePratique)) {
        return
      }

      this.messageSelector = "Save"
      this.overlay = true

      // Autorisation de pratique
      for (const element of this.listeAutorisationsDePratique) {
        await this.updateAutorisationDePratique(element)
      }

      // Autre Autorisation de pratique
      if (this.demandeAdmissionId) {
        this.getAutresAutorisationsDePratique.demandeInscription = this.demandeInscriptionId
      }

      if (this.contactId) {
        this.getAutresAutorisationsDePratique.pp = this.contactId
      }

      if (!this.getAutresAutorisationsDePratique.id) {
        await this.createAutresAutorisationsDePratique(this.getAutresAutorisationsDePratique)
      } else {
        await this.updateAutresAutorisationsDePratique(this.getAutresAutorisationsDePratique)
      }

      this.next()
    },
    afficherAlert(item) {
      this.autorisationASupprimer = item
      // Retirer la dernière autorisation active vaut démission : le formulaire s'ouvre tout de suite, sans passer par Suivant
      this.dialog = this.estDerniereAutorisationActive(item)
      this.dialogRetraitAutorisationUnique = !this.dialog
    },
    annulerDemission() {
      this.dialog = false
      this.autorisationASupprimer = null
    },
    estDerniereAutorisationActive(item) {
      return this.listeAutorisationsDePratique.filter((a) => a !== item && !this.isRetraitVolontaire(a)).length === 0
    },
    deleteRow() {
      if (this.autorisationASupprimer != null) {
        this.autorisationASupprimer.raisonRadiation = {
          id: this.$catalogues.motifRadiationAutorisationDePatique.retraitVolontaire,
        }
        this.autorisationASupprimer.deleted = true
        this.$forceUpdate()
      }
      this.dialogRetraitAutorisationUnique = false
    },
    addRow(item) {
      item.raisonRadiation = null
    },
    itemText: (item) => item.lang00,
    itemValue: (item) => item.id,
    isRetraitVolontaire(item) {
      return item.raisonRadiation?.id === this.$catalogues.motifRadiationAutorisationDePatique.retraitVolontaire
    },
    canDelete(item) {
      return !item.statut || item.demandeInscription == this.demandeInscriptionId
    },
    validateForm(resetValidation = false) {
      this.valid =
        (this.$refs.form?.validate() || false) &&
        // check lists is not null
        !!this.getAutorisations &&
        !!this.getAutresAutorisationsDePratique

      if (resetValidation) {
        this.$refs.form?.resetValidation()
      }
    },
    async processRetraitVolontaire() {
      if (!this.$refs.formDemission?.validate()) {
        return
      }

      // Popup ouvert depuis la corbeille : l'autorisation est retirée avant la clôture
      if (this.autorisationASupprimer != null) {
        this.deleteRow()
      }

      // Confirmer la démission clôt le renouvellement sur-le-champ, sans repasser par « Suivant »
      await this.terminerDemission()
    },
    async terminerDemission() {
      this.dialog = false
      this.messageSelector = "Save"
      this.overlay = true

      for (const element of this.listeAutorisationsDePratique) {
        await this.updateAutorisationDePratique(element)
      }

      await this.demission(this.champsDemission)
      await this.retraitVolontaire()
      await this.demandeTerminee()

      await this.$router.push({
        name: `DemandeInscription/RetraitVolontaire`,
      })
    },
  },
}
</script>

<style></style>